import { convertMvToPercent } from "../../../utils/dataTransforms";
import {
  formatDate,
  formatSpeed,
  formatTemperature,
  formatDecimal,
} from "../../../utils/formatters";
import { logger } from "../../../utils/logger";
import { parseJsonSafe } from "../../../utils/json";

const escapeHtml = (unsafe) => {
  if (unsafe === null || unsafe === undefined) {
    return "";
  }

  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

export const createEnhancedPopupContent = ({
  timeCreated,
  geocode,
  battery,
  deviceBrand,
  rawData,
  latitude,
  longitude,
  primaryColor = "#667eea",
  isDarkMode = false,
  title,
  statusColor: _statusColor,
  showSensorGrid = true,
  showCoordinates = true,
}) => {
  let data = parseJsonSafe(rawData || "{}", null);
  if (!data) {
    logger.warn("Error parsing raw data for popup");
    data = {};
  }

  const resolvedTitle =
    title && String(title).trim().length
      ? escapeHtml(title)
      : "📍 Device Location";

  const textColor = isDarkMode ? "#f9fafb" : "#1f2937";
  const panelBg = isDarkMode ? "rgba(255,255,255,0.08)" : "#f9fafb";
  const popupBg = isDarkMode ? "#1f2937" : "#ffffff";

  let content = `
    <style>
      .mapboxgl-popup-content {
        background: ${popupBg} !important;
        color: ${textColor} !important;
        padding: 15px !important;
        border-radius: 8px !important;
      }
      .popup-container { max-width: 300px; font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; }
      .popup-header {
        background: ${isDarkMode ? "#374151" : primaryColor};
        color: #fff;
        padding: 12px 16px;
        margin: -15px -15px 12px -15px;
        border-radius: 8px 8px 0 0;
      }
      .popup-title { font-weight: 600; font-size: 14px; margin: 0 0 4px 0; }
      .popup-time { font-size: 12px; opacity: 0.9; margin: 0; }
      .popup-value { font-size: 13px; padding: 8px 12px; background: ${panelBg}; border-radius: 6px; border-left: 3px solid ${primaryColor}; margin-bottom: 8px; }
      .popup-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
      .popup-grid-item { background: ${panelBg}; padding: 8px 10px; border-radius: 6px; border-left: 2px solid ${primaryColor}; }
      .popup-grid-label { font-size: 10px; font-weight: 600; color: ${isDarkMode ? "#9ca3af" : "#6b7280"}; text-transform: uppercase; margin-bottom: 3px; }
      .popup-grid-value { font-size: 13px; color: ${textColor}; font-weight: 500; }
      .coordinates { font-family: 'Courier New', monospace; font-size: 11px; color: ${isDarkMode ? "#9ca3af" : "#6b7280"}; background: ${panelBg}; padding: 8px 10px; border-radius: 6px; margin-top: 8px; }
    </style>
    <div class="popup-container">
      <div class="popup-header">
        <div class="popup-title">${resolvedTitle}</div>
        <div class="popup-time">${formatDate(timeCreated)}</div>
      </div>
  `;

  if (geocode) {
    content += `<div class="popup-value">${escapeHtml(geocode)}</div>`;
  }

  if (showSensorGrid) {
    const entries = [];
    if (battery) {
      entries.push([
        "Battery",
        `🔋 ${convertMvToPercent(battery, deviceBrand)}`,
      ]);
    }
    if (data.speedKmph !== undefined) {
      entries.push(["Speed", `🚗 ${formatSpeed(data.speedKmph)}`]);
    }
    if (data.temperature !== undefined) {
      entries.push([
        "Temperature",
        `🌡️ ${formatTemperature(data.temperature, true)}`,
      ]);
    }
    if (data.humidity_1 !== undefined) {
      entries.push(["Humidity 1", `💧 ${formatDecimal(data.humidity_1)}%`]);
    }
    if (data.altitude !== undefined) {
      entries.push(["Altitude", `⛰️ ${formatDecimal(data.altitude)}m`]);
    }
    if (data.headingDeg !== undefined) {
      entries.push(["Heading", `🧭 ${formatDecimal(data.headingDeg)}°`]);
    }
    if (data.manDown !== undefined) {
      entries.push(["Man Down", `🚨 ${data.manDown ? "Yes" : "No"}`]);
    }

    if (entries.length) {
      content += '<div class="popup-grid">';
      for (const [label, value] of entries) {
        content += `<div class="popup-grid-item"><div class="popup-grid-label">${label}</div><div class="popup-grid-value">${value}</div></div>`;
      }
      content += "</div>";
    }
  }

  if (showCoordinates && latitude && longitude) {
    content += `<div class="coordinates">📍 ${latitude.toFixed(6)}, ${longitude.toFixed(6)}</div>`;
  }

  content += "</div>";
  return content;
};

export const createGeofencePopupContent = ({
  name,
  deviceCount,
  historyItems = [],
  primaryColor = "#667eea",
  isDarkMode = false,
}) => {
  const textColor = isDarkMode ? "#f9fafb" : "#1f2937";
  const panelBg = isDarkMode ? "rgba(255,255,255,0.08)" : "#f9fafb";
  const popupBg = isDarkMode ? "#1f2937" : "#ffffff";
  const mutedColor = isDarkMode ? "#9ca3af" : "#6b7280";
  const showDeviceCount = deviceCount !== null && deviceCount !== undefined;
  const safeCount = showDeviceCount ? Number(deviceCount) || 0 : 0;
  const label = safeCount === 1 ? "device inside" : "devices inside";
  const devicesHtml = showDeviceCount
    ? `<div class="popup-value"><div class="popup-value-label">Devices</div><div class="popup-value-data">${safeCount} ${label}</div></div>`
    : "";

  const relativeTime = (ts) => {
    if (!ts) return "";
    const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  let historyHtml = "";
  if (historyItems.length) {
    const rows = historyItems
      .map((ev) => {
        const icon = ev.event_type === "enter" ? "↗" : "↙";
        const color = ev.event_type === "enter" ? "#22c55e" : "#ef4444";
        const action = ev.event_type === "enter" ? "entered" : "exited";
        return `
          <div class="gf-history-row">
            <span class="gf-history-icon" style="color:${color}">${icon}</span>
            <span class="gf-history-name">${escapeHtml(ev.deviceName || `Device ${ev.device_id}`)}</span>
            <span class="gf-history-action" style="color:${color}">${action}</span>
            <span class="gf-history-time">${relativeTime(ev.occurred_at)}</span>
          </div>`;
      })
      .join("");

    historyHtml = `
      <div class="popup-value" style="padding:0; overflow:hidden;">
        <div class="popup-value-label" style="padding: 6px 12px 4px;"">Recent Activity</div>
        <div class="gf-history-list">${rows}</div>
      </div>`;
  }

  return `
    <style>
      .mapboxgl-popup-content {
        background: ${popupBg} !important;
        color: ${textColor} !important;
        padding: 15px !important;
        border-radius: 8px !important;
      }
      .popup-container { max-width: 280px; font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; }
      .popup-header {
        background: ${isDarkMode ? "#374151" : primaryColor};
        color: #fff;
        padding: 12px 16px;
        margin: -15px -15px 12px -15px;
        border-radius: 8px 8px 0 0;
      }
      .popup-title { font-weight: 600; font-size: 14px; margin: 0; }
      .popup-value { font-size: 13px; padding: 8px 12px; background: ${panelBg}; border-radius: 6px; border-left: 3px solid ${primaryColor}; margin-bottom: 8px; color: ${textColor}; }
      .popup-value-label { font-size: 10px; font-weight: 600; color: ${mutedColor}; text-transform: uppercase; margin-bottom: 3px; }
      .popup-value-data { font-size: 13px; font-weight: 500; }
      .gf-history-list { display: flex; flex-direction: column; }
      .gf-history-row { display: grid; grid-template-columns: 16px 1fr auto auto; align-items: center; gap: 6px; padding: 5px 12px; font-size: 12px; border-top: 1px solid ${isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}; }
      .gf-history-icon { font-size: 13px; font-weight: 700; }
      .gf-history-name { font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: ${textColor}; }
      .gf-history-action { font-size: 11px; white-space: nowrap; }
      .gf-history-time { font-size: 11px; color: ${mutedColor}; white-space: nowrap; }
    </style>
    <div class="popup-container">
      <div class="popup-header">
        <div class="popup-title">${escapeHtml(name || "Geofence")}</div>
      </div>
      ${devicesHtml}
      ${historyHtml}
    </div>
  `;
};
